import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/leave/types")({
  component: LeaveTypesDataTable,
});

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";

import { SelectFilter } from "@/components/crud/filters/SelectFilter";

import { useDebounce } from "@/hooks/use-debounce";
import dayjs from "dayjs";
// Leave Hooks
import useFetchLeaves, {
  type LeaveOrdering,
} from "@/hooks/api/leave/leave/useFetchLeaves";
import useCreateLeave from "@/hooks/api/leave/leave/useCreateLeave";
import useUpdateLeave from "@/hooks/api/leave/leave/useUpdateLeave";
import useDeleteLeave from "@/hooks/api/leave/leave/useDeleteLeave";
import { LeaveTypeTable } from "@/components/crud/table/LeaveTypeTable";
import type { Leave, LeavePayload } from "@/types/leave";
import { LeaveTypeFormSheet } from "@/components/crud/forms/LeaveTypeFormSheet";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import type { DateRange } from "react-day-picker";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function LeaveTypesDataTable() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "leave.type.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Filters and Search
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        is_active: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      LeaveOrdering
    >({
      name: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });
  const [statusFilter, setStatusFilter] = useState<
    "true" | "false" | "clear" | undefined
  >();
  const [startDateFilter, setStartDateFilter] = useState<string | undefined>();
  const [endDateFilter, setEndDateFilter] = useState<string | undefined>();
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);

  // UI state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState<Leave | null>(null);
  const [editingLeaveId, setEditingLeaveId] = useState<number | null>(null);
  const [leaveToDelete, setLeaveToDelete] = useState<Leave | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // CRUD hooks
  const leavesQuery = useFetchLeaves({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      statusFilter === "clear"
        ? undefined
        : statusFilter === "true"
          ? true
          : statusFilter === "false"
            ? false
            : undefined,
    start_date: startDateFilter
      ? dayjs(startDateFilter).format("YYYY-MM-DD")
      : undefined,
    end_date: endDateFilter
      ? dayjs(endDateFilter).format("YYYY-MM-DD")
      : undefined,
  });
  const leaves = leavesQuery.data?.results ?? [];
  const count = leavesQuery.data?.count ?? 0;

  const createLeave = useCreateLeave();
  const updateLeave = useUpdateLeave();
  const deleteLeave = useDeleteLeave();

  const isMutating =
    createLeave.isPending || updateLeave.isPending || deleteLeave.isPending;
  const isLoading = leavesQuery.isPending || isMutating;
  const isFetching = leavesQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    startDateFilter,
    endDateFilter,
    setPage,
  ]);

  // For resetting after create, update and delete
  const resetFilters = () => {
    setDateRange(undefined);
    setOrdering({
      name: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });
    setStatusFilter(undefined);
    setStartDateFilter(undefined);
    setEndDateFilter(undefined);
    setGlobalFilter("");
    resetDebounce();
    setPage(1);
  };

  // Handlers
  const handleCreate = () => {
    setEditingLeave(null);
    setEditingLeaveId(null);
    setIsSheetOpen(true);
  };

  const handleEdit = (leave: Leave) => {
    setEditingLeaveId(leave.id);
    setEditingLeave({ ...leave });
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (leave: Leave) => {
    setLeaveToDelete(leave);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leaveToDelete) return;
    try {
      await deleteLeave.mutateAsync({ id: leaveToDelete.id });
      toast.success("Leave deleted successfully!");
      setIsDeleteDialogOpen(false);
      setLeaveToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: LeavePayload) => {
    if (editingLeaveId) {
      await updateLeave.mutateAsync({ id: editingLeaveId, payload: data });
      toast.success("Leave updated successfully!");
    } else {
      await createLeave.mutateAsync(data);
      toast.success("Leave created successfully!");
    }
    setIsSheetOpen(false);
  };

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  useEffect(() => {
    setStartDateFilter(
      dateRange?.from ? dayjs(dateRange.from).format("YYYY-MM-DD") : undefined
    );

    setEndDateFilter(
      dateRange?.to ? dayjs(dateRange.to).format("YYYY-MM-DD") : undefined
    );
  }, [dateRange]);

  const [showFilters, setShowFilters] = useState(false);

  // Add the View Mode state
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Handler for the View button (from the table)
  const handleView = (leave: Leave) => {
    setEditingLeave(leave);
    setEditingLeaveId(null); // Usually good to clear this in view mode
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };
  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Leave Types"
          description=" Manage, create, and organize different types of leave."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Leave"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search..."
            value={globalFilter}
            onChange={setGlobalFilter}
          />

          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Not Active", value: "false" },
              { label: "All", value: "clear" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />

          {/* selects date in range  */}
          <DateSelector
            value={dateRange}
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(undefined)}
          />
        </FiltersPanel>

        <LeaveTypeTable
          leaves={leaves}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          canManage={canManage}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <LeaveTypeFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingLeave={editingLeave}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Leave"
          description={`Are you sure you want to delete this leave with the name "${leaveToDelete?.name}"? This action cannot be undone.`}
          itemName={leaveToDelete?.name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
